import { Add, ArrowDownward, ArrowUpward, DeleteOutline, ViewInArOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import SinDatos from "components/Carteles/SinDatos";
import CabeceraPasoEvento from "../CabeceraPasoEvento";
import SelectorProductosEvento from "../SelectorProductosEvento";
import { PASOS, PRECIO_MAXIMO_EVENTO, formatoPesos, productosHabilitados } from "../../utils";

const COLUMNAS = "minmax(0, 2.6fr) minmax(105px, 1fr) minmax(170px, 1.8fr) 140px 112px";
const filaSx = {
  display: "grid", alignItems: "center", columnGap: 1.5,
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: COLUMNAS },
  px: { xs: 1.5, sm: 2 }, py: 1,
};
const etiquetaMovilSx = { display: { xs: "block", md: "none" }, fontSize: 10.5, fontWeight: 700, color: "#65708B", mb: 0.5 };
const precioSx = {
  "& .MuiOutlinedInput-root": { display: "flex !important", p: "0 !important", minHeight: 32, borderRadius: "9px !important", borderColor: "#D6D4E7", background: "linear-gradient(180deg, #fff, #FCFBFF)" },
  "& .MuiInputBase-input": { fontSize: 12, p: "5px 10px !important", width: "100% !important", minWidth: 0, flex: "1 1 0", boxSizing: "border-box" },
  "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error)": { borderColor: "#7953DE" },
  "& .MuiOutlinedInput-root.Mui-error": { borderColor: "#E84B65" },
  "& .MuiOutlinedInput-root.Mui-disabled": { background: "#F4F3F8" },
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
};
const estadoSx = {
  width: 38, height: 20, flexShrink: 0, mr: 0,
  "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 2px rgba(0,0,0,.12)" },
  "& .MuiSwitch-switchBase": { transform: "translate(2px, -50%)" },
  "& .MuiSwitch-switchBase.Mui-checked": { transform: "translate(20px, -50%)" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#10B5A1 !important", borderColor: "#10B5A1 !important" },
};

const accion = (titulo, Icono, onClick, disabled = false) => (
  <Tooltip title={titulo} placement="top">
    <span>
      <IconButton aria-label={titulo} disabled={disabled} size="small" onClick={onClick} sx={{
        width: 30, height: 30, border: "1px solid #E5E2F1", borderRadius: "9px", color: "#403877",
        background: "linear-gradient(180deg, #fff, #F7F6FB)", "&:hover": { background: "#ECE7FC", borderColor: "#CFC3F1" },
        "&.Mui-disabled": { color: "#B5B1CA", borderColor: "#EEEAF6" },
      }}><Icono sx={{ fontSize: 18 }} /></IconButton>
    </span>
  </Tooltip>
);

export default function PasoProductos({ form, setField, errores, categorias, idsConVentas, soloLectura }) {
  const [selectorAbierto, setSelectorAbierto] = useState(false);
  const productos = form.productos;
  const vendidos = new Set((idsConVentas || []).map(String));
  const cambiar = (index, campo, valor) =>
    setField("productos", productos.map((item, i) => (i === index ? { ...item, [campo]: valor } : item)));
  const mover = (index, delta) => {
    const siguiente = [...productos];
    [siguiente[index], siguiente[index + delta]] = [siguiente[index + delta], siguiente[index]];
    setField("productos", siguiente);
  };
  const agregar = (nuevos) => {
    setField("productos", [
      ...productos,
      ...nuevos.map((producto) => ({ idProducto: String(producto.id), producto, precioEvento: "", habilitado: true })),
    ]);
    setSelectorAbierto(false);
  };

  return (
    <Box>
      <CabeceraPasoEvento paso={PASOS.findIndex((paso) => paso.id === "productos")} acciones={(
        <>
          <Tooltip title="Deje el precio de venta vacío para usar el precio de carta.">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, minHeight: 38, borderRadius: "10px", color: "#29205F", background: "linear-gradient(110deg, #F1ECFF, #F4F0FD)" }}>
              <ViewInArOutlined sx={{ fontSize: 21, color: "#4931B3" }} />
              <Typography sx={{ fontSize: 12 }}>{productos.length} producto(s) · {productosHabilitados(form).length} a la venta</Typography>
            </Box>
          </Tooltip>
          {!soloLectura && <Button variant="contained" startIcon={<Add />} onClick={() => setSelectorAbierto(true)}
            sx={{ minHeight: "38px !important", px: 2, "& .MuiButton-startIcon svg": { fontSize: 19 } }}>Agregar productos</Button>}
        </>
      )} />
      {errores.productos && <Typography role="alert" sx={{ color: "#D94264", fontSize: 12, mb: 1 }}>{errores.productos}</Typography>}

      {productos.length ? (
        <Box role="table" aria-label="Productos del evento" sx={{ border: "1px solid #E4DDF9", borderRadius: "12px", overflow: "hidden" }}>
          <Box role="rowgroup" sx={{ display: { xs: "none", md: "block" } }}>
            <Box role="row" sx={{ ...filaSx, py: 1, background: "linear-gradient(100deg, #EFE8FC, #EEE9FA)", color: "#211941", "& > [role=columnheader]": { fontSize: 12, fontWeight: 700 } }}>
              <Box role="columnheader">Producto</Box>
              <Box role="columnheader">Precio de carta</Box>
              <Box role="columnheader">Precio de venta</Box>
              <Box role="columnheader">Estado</Box>
              <Box role="columnheader" aria-label="Acciones" />
            </Box>
          </Box>
          <Box role="rowgroup">
            {productos.map((item, index) => {
              const nombre = item.producto?.nombre || "Producto #" + item.idProducto;
              const vendido = vendidos.has(String(item.idProducto));
              const sinPrecio = (item.precioEvento === "" || item.precioEvento == null) && !(Number(item.producto?.precioCarta) > 0);
              return (
                <Box key={item.idProducto} role="row" aria-label={nombre} data-testid={"producto-evento-" + item.idProducto}
                  sx={{ ...filaSx, rowGap: { xs: 1.25, md: 0 }, minHeight: 58, borderBottom: index < productos.length - 1 ? "1px solid #EEE9FA" : 0,
                    background: item.habilitado ? "linear-gradient(100deg, #fff, #FCFBFE)" : "#F5F4F9" }}>
                  <Box role="cell" sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: item.habilitado ? "#171332" : "#727086", overflowWrap: "anywhere" }}>{nombre}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#777596", mt: 0.2 }}>{item.producto?.categoriaProducto?.categoria || ""}</Typography>
                  </Box>
                  <Box role="cell" minWidth={0}>
                    <Typography sx={etiquetaMovilSx}>Precio de carta</Typography>
                    <Box component="span" sx={{ display: "inline-block", px: 1.25, py: 0.5, borderRadius: "8px", background: "#EEECF5", color: "#28204E", fontSize: 12, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {item.producto?.precioCarta != null ? formatoPesos(item.producto.precioCarta) : "—"}
                    </Box>
                  </Box>
                  <Box role="cell" minWidth={0}>
                    <Typography sx={etiquetaMovilSx}>Precio de venta</Typography>
                    <TextField type="number" size="small" fullWidth value={item.precioEvento} placeholder={sinPrecio ? "Sin precio de carta" : "Precio de carta"}
                      onChange={(e) => cambiar(index, "precioEvento", e.target.value)} error={item.habilitado && sinPrecio} disabled={soloLectura} sx={precioSx}
                      helperText={item.habilitado && sinPrecio ? "Indique un precio de venta." : undefined}
                      inputProps={{ min: 0.01, max: PRECIO_MAXIMO_EVENTO, step: 0.01, "aria-label": "Precio en el evento de " + nombre }}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1.25, mr: 0 }}><Typography sx={{ fontSize: 14, fontWeight: 700, color: "#261E51" }}>$</Typography></InputAdornment> }} />
                  </Box>
                  <Box role="cell" sx={{ display: "flex", alignItems: "center", gap: 0.875, pl: { md: 1.5 }, borderLeft: { md: "1px solid #EAE5F5" } }}>
                    <Switch checked={item.habilitado} disabled={soloLectura} onChange={(_, value) => cambiar(index, "habilitado", value)}
                      inputProps={{ "aria-label": nombre + " a la venta" }} sx={estadoSx} />
                    <Typography sx={{ fontSize: 12, color: "#282046", whiteSpace: "nowrap" }}>{item.habilitado ? "A la venta" : "Pausado"}</Typography>
                  </Box>
                  <Box role="cell" sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                    {!soloLectura && <>
                      {accion("Subir", ArrowUpward, () => mover(index, -1), index === 0)}
                      {accion("Bajar", ArrowDownward, () => mover(index, 1), index === productos.length - 1)}
                      {accion(vendido ? "Tiene ventas en el evento: páuselo en lugar de quitarlo" : "Quitar del evento", DeleteOutline,
                        () => setField("productos", productos.filter((_, i) => i !== index)), vendido)}
                    </>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <SinDatos titulo="Sin productos" mensaje="Agregue productos o habilite el menú universitario en el paso siguiente." icono="inventory_2" />
      )}

      <SelectorProductosEvento open={selectorAbierto} categorias={categorias}
        idsExistentes={productos.map((item) => item.idProducto)}
        onCancel={() => setSelectorAbierto(false)} onConfirm={agregar} />
    </Box>
  );
}

PasoProductos.propTypes = {
  form: PropTypes.object.isRequired,
  setField: PropTypes.func.isRequired,
  errores: PropTypes.object.isRequired,
  categorias: PropTypes.array.isRequired,
  idsConVentas: PropTypes.arrayOf(PropTypes.string),
  soloLectura: PropTypes.bool,
};

PasoProductos.defaultProps = { idsConVentas: [], soloLectura: false };
